import { useEffect, useMemo, useState } from "react"
import { motion } from "framer-motion"
import { X, Plus, BookTemplate, Save, Dumbbell, Repeat, CalendarRange, Search, AlertCircle } from "lucide-react"
import { createRoutineSeries, getTemplates } from "../../api/routines"
import type { Routine } from "../../types"

const DAY_OPTIONS = [
  { value: 1, label: "Lunes", short: "Lun" },
  { value: 2, label: "Martes", short: "Mar" },
  { value: 3, label: "Miércoles", short: "Mié" },
  { value: 4, label: "Jueves", short: "Jue" },
  { value: 5, label: "Viernes", short: "Vie" },
  { value: 6, label: "Sábado", short: "Sáb" },
  { value: 0, label: "Domingo", short: "Dom" },
]

// Búsqueda sin acentos ni mayúsculas: "dia de pierna" encuentra "Día de Pierna".
const DIACRITICS = /[\u0300-\u036f]/g

function normalize(s: string): string {
  return s.toLowerCase().normalize("NFD").replace(DIACRITICS, "")
}

function localDateStr(d: Date): string {
  const y = d.getFullYear()
  const m = String(d.getMonth() + 1).padStart(2, "0")
  const day = String(d.getDate()).padStart(2, "0")
  return `${y}-${m}-${day}`
}

type Mode = "new" | "gallery"

export default function AssignRoutineModal({
  clientId,
  defaultDate,
  defaultHour,
  defaultMode = "new",
  onClose,
  onCreated,
}: {
  clientId: number
  defaultDate: Date
  defaultHour?: number
  defaultMode?: Mode
  onClose: () => void
  onCreated: (routines: Routine[]) => void
}) {
  const [mode, setMode] = useState<Mode>(defaultMode)
  const [name, setName] = useState("")

  // Recurrencia: días de la semana (default = el día del slot clickeado) + nº de semanas.
  const [selectedDays, setSelectedDays] = useState<number[]>([defaultDate.getDay()])
  const [weeks, setWeeks] = useState(1)
  const [startHour, setStartHour] = useState<number>(defaultHour ?? 9)

  // Galería
  const [templates, setTemplates] = useState<Routine[]>([])
  const [loadingTpls, setLoadingTpls] = useState(false)
  const [selectedTpl, setSelectedTpl] = useState<Routine | null>(null)
  const [tplSearch, setTplSearch] = useState("")

  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (mode === "gallery" && templates.length === 0) {
      setLoadingTpls(true)
      getTemplates()
        .then((t) => setTemplates(Array.isArray(t) ? t : []))
        .finally(() => setLoadingTpls(false))
    }
  }, [mode, templates.length])

  // Busca por nombre de plantilla y también por nombre de ejercicio: los ejercicios
  // ya vienen incluidos en la respuesta, así que filtrar por ellos no cuesta nada.
  const filteredTemplates = useMemo(() => {
    const q = normalize(tplSearch.trim())
    if (!q) return templates
    return templates.filter(
      (t) =>
        normalize(t.name).includes(q) ||
        t.routine_exercises.some((re) => normalize(re.exercise.name).includes(q))
    )
  }, [templates, tplSearch])

  // Si la plantilla elegida queda fuera del filtro, se muestra aparte para que no
  // parezca que se perdió la selección.
  const selectedHidden =
    selectedTpl !== null && !filteredTemplates.some((t) => t.id === selectedTpl.id)

  function toggleDay(v: number) {
    setSelectedDays((prev) =>
      prev.includes(v) ? prev.filter((d) => d !== v) : [...prev, v]
    )
  }

  const canSubmit =
    selectedDays.length > 0 &&
    weeks >= 1 &&
    (mode === "new" ? name.trim().length > 0 : selectedTpl !== null)

  async function handleSubmit() {
    if (!canSubmit) return
    setSaving(true)
    setError(null)
    try {
      const base: Record<string, unknown> = {
        start_hour: startHour,
        end_hour: startHour + 1,
      }
      if (mode === "new") {
        base.name = name.trim()
      } else if (selectedTpl) {
        // template_id deja la rutina vinculada a la plantilla: editar la plantilla
        // en la galería propaga los cambios a este programa.
        base.template_id = selectedTpl.id
        base.name = selectedTpl.name
        base.description = selectedTpl.description
        base.routine_exercises_attributes = selectedTpl.routine_exercises.map((re) => ({
          exercise_id: re.exercise.id,
          sets: re.sets,
          reps: re.reps,
          reps_unit: re.reps_unit,
          weight: re.weight,
          weight_unit: re.weight_unit,
        }))
      }

      const created = await createRoutineSeries(
        base,
        { start_date: localDateStr(defaultDate), weekdays: selectedDays, weeks },
        clientId
      )
      onCreated(created)
    } catch (e) {
      // Sin esto el fallo era invisible: el modal quedaba abierto sin decir nada y
      // parecía que el botón "no hacía nada".
      const err = e as { response?: { data?: { errors?: string[] } } }
      setError(err.response?.data?.errors?.join(". ") || "No se pudo asignar la rutina. Intentá de nuevo.")
    } finally {
      setSaving(false)
    }
  }

  // Resumen de cuántas rutinas se crearán (orientativo).
  const occurrenceCount = selectedDays.length * weeks

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      onClick={onClose}
      className="fixed inset-0 z-50 flex items-center justify-center p-4 backdrop-blur-md"
      style={{
        background:
          "radial-gradient(circle at 50% 40%, rgba(45,212,191,0.18), rgba(8,10,14,0.86) 60%, rgba(0,0,0,0.92))",
      }}
    >
      <motion.div
        initial={{ scale: 0.92, opacity: 0, y: 10 }}
        animate={{ scale: 1, opacity: 1, y: 0 }}
        exit={{ scale: 0.92, opacity: 0, y: 10 }}
        transition={{ type: "spring", stiffness: 300, damping: 26 }}
        onClick={(e) => e.stopPropagation()}
        className="gradient-card glow-cyan w-full max-w-lg overflow-hidden rounded-2xl border border-primary/30 shadow-2xl"
      >
        {/* Header */}
        <div className="flex items-center justify-between border-b border-white/10 px-5 py-4">
          <div>
            <h3 className="text-base font-semibold text-white">Asignar rutina</h3>
            <p className="text-xs text-muted capitalize">
              {defaultDate.toLocaleDateString("es-CR", { weekday: "long", day: "numeric", month: "long" })}
            </p>
          </div>
          <button
            onClick={onClose}
            className="rounded-lg p-2 text-muted transition hover:bg-white/10 hover:text-white"
          >
            <X size={18} />
          </button>
        </div>

        {/* Tabs: Nueva / Galería */}
        <div className="flex gap-1 px-5 pt-4">
          <button
            onClick={() => setMode("new")}
            className={`flex flex-1 items-center justify-center gap-2 rounded-lg border px-3 py-2 text-sm font-medium transition ${
              mode === "new"
                ? "border-primary/40 bg-primary/15 text-primary"
                : "border-white/10 text-muted hover:text-white"
            }`}
          >
            <Plus size={15} />
            Nueva rutina
          </button>
          <button
            onClick={() => setMode("gallery")}
            className={`flex flex-1 items-center justify-center gap-2 rounded-lg border px-3 py-2 text-sm font-medium transition ${
              mode === "gallery"
                ? "border-primary/40 bg-primary/15 text-primary"
                : "border-white/10 text-muted hover:text-white"
            }`}
          >
            <BookTemplate size={15} />
            Importar de galería
          </button>
        </div>

        <div className="space-y-4 px-5 py-4">
          {/* Contenido según modo */}
          {mode === "new" ? (
            <div className="flex flex-col gap-1">
              <label className="px-1 text-xs text-muted">Nombre de la rutina</label>
              <input
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Ej: Día de Piernas"
                autoFocus
                className="rounded-lg border border-white/10 bg-surface-elevated px-3 py-2.5 text-sm text-white focus:border-primary"
              />
            </div>
          ) : (
            <div className="flex flex-col gap-1">
              <div className="flex items-baseline justify-between px-1">
                <label className="text-xs text-muted">Plantilla de la galería</label>
                {templates.length > 0 && tplSearch.trim().length > 0 && (
                  <span className="text-xs text-muted/70">
                    {filteredTemplates.length} de {templates.length}
                  </span>
                )}
              </div>
              {loadingTpls ? (
                <div className="flex items-center justify-center py-6">
                  <Dumbbell size={22} className="animate-pulse-glow text-primary" />
                </div>
              ) : templates.length === 0 ? (
                <p className="rounded-lg border border-white/10 bg-surface-elevated px-3 py-4 text-center text-xs text-muted">
                  No hay plantillas. Creá una desde la Galería de Rutinas.
                </p>
              ) : (
                <>
                  <div className="relative mb-1.5">
                    <Search
                      size={14}
                      className="absolute left-3 top-1/2 -translate-y-1/2 text-muted"
                    />
                    <input
                      value={tplSearch}
                      onChange={(e) => setTplSearch(e.target.value)}
                      placeholder="Buscar por nombre o ejercicio..."
                      autoFocus
                      className="w-full rounded-lg border border-white/10 bg-surface-elevated py-2 pl-9 pr-8 text-sm text-white focus:border-primary"
                    />
                    {tplSearch && (
                      <button
                        onClick={() => setTplSearch("")}
                        className="absolute right-2 top-1/2 -translate-y-1/2 rounded p-1 text-muted transition hover:text-white"
                      >
                        <X size={13} />
                      </button>
                    )}
                  </div>

                  {selectedHidden && selectedTpl && (
                    <div className="mb-1.5 flex items-center gap-2 rounded-lg border border-primary/50 bg-primary/10 px-3 py-2">
                      <div className="min-w-0 flex-1">
                        <p className="truncate text-sm font-medium text-white">{selectedTpl.name}</p>
                        <p className="text-xs text-muted">Seleccionada (fuera de la búsqueda)</p>
                      </div>
                      <button
                        onClick={() => setSelectedTpl(null)}
                        className="rounded p-1 text-muted transition hover:text-white"
                      >
                        <X size={14} />
                      </button>
                    </div>
                  )}

                  {filteredTemplates.length === 0 ? (
                    <p className="rounded-lg border border-white/10 bg-surface-elevated px-3 py-4 text-center text-xs text-muted">
                      Ninguna plantilla coincide con la búsqueda.
                    </p>
                  ) : (
                    <div className="max-h-56 space-y-1.5 overflow-y-auto pr-1">
                      {filteredTemplates.map((tpl) => (
                        <button
                          key={tpl.id}
                          onClick={() => setSelectedTpl(tpl)}
                          className={`w-full rounded-lg border px-3 py-2 text-left transition ${
                            selectedTpl?.id === tpl.id
                              ? "border-primary/50 bg-primary/10"
                              : "border-white/10 bg-surface-elevated hover:border-primary/30"
                          }`}
                        >
                          <p className="truncate text-sm font-medium text-white">{tpl.name}</p>
                          <p className="text-xs text-muted">
                            {tpl.routine_exercises.length} ejercicio{tpl.routine_exercises.length !== 1 ? "s" : ""}
                          </p>
                        </button>
                      ))}
                    </div>
                  )}
                </>
              )}
            </div>
          )}

          {/* Días de la semana */}
          <div className="flex flex-col gap-1.5">
            <label className="px-1 text-xs text-muted">Días de la semana</label>
            <div className="flex flex-wrap gap-1.5">
              {DAY_OPTIONS.map((d) => {
                const active = selectedDays.includes(d.value)
                return (
                  <button
                    key={d.value}
                    onClick={() => toggleDay(d.value)}
                    className={`h-9 w-11 rounded-lg border text-xs font-medium transition ${
                      active
                        ? "border-primary bg-primary text-black"
                        : "border-white/10 text-muted hover:border-primary/40 hover:text-white"
                    }`}
                  >
                    {d.short}
                  </button>
                )
              })}
            </div>
          </div>

          {/* Hora + Repetir semanas */}
          <div className="flex gap-3">
            <div className="flex flex-1 flex-col gap-1">
              <label className="px-1 text-xs text-muted">Hora de inicio</label>
              <select
                value={startHour}
                onChange={(e) => setStartHour(Number(e.target.value))}
                className="rounded-lg border border-white/10 bg-surface-elevated px-3 py-2 text-sm text-white focus:border-primary"
              >
                {Array.from({ length: 15 }, (_, i) => i + 6).map((h) => (
                  <option key={h} value={h}>
                    {String(h).padStart(2, "0")}:00–{String(h + 1).padStart(2, "0")}:00
                  </option>
                ))}
              </select>
            </div>
            <div className="flex w-24 flex-col gap-1">
              <label className="flex items-center gap-1 px-1 text-xs text-muted">
                <Repeat size={11} /> Semanas
              </label>
              <input
                type="number"
                min={1}
                max={52}
                value={weeks}
                onChange={(e) => setWeeks(Math.max(1, Math.min(52, Number(e.target.value) || 1)))}
                className="w-full rounded-lg border border-white/10 bg-surface-elevated px-3 py-2 text-sm text-white focus:border-primary"
              />
            </div>
          </div>

          {/* Resumen */}
          <p className="flex items-center gap-1.5 text-xs text-muted">
            <CalendarRange size={13} className="text-primary/70" />
            Se crearán <span className="font-semibold text-primary">{occurrenceCount}</span> rutina
            {occurrenceCount !== 1 ? "s" : ""} ({selectedDays.length} día{selectedDays.length !== 1 ? "s" : ""} ×{" "}
            {weeks} semana{weeks !== 1 ? "s" : ""}).
          </p>
        </div>

        {/* Footer */}
        {error && (
          <div className="flex items-start gap-2 border-t border-danger/30 bg-danger/10 px-5 py-3 text-xs text-danger">
            <AlertCircle size={14} className="mt-0.5 shrink-0" />
            <span>{error}</span>
          </div>
        )}
        <div className="flex gap-2 border-t border-white/10 px-5 py-4">
          <button
            onClick={handleSubmit}
            disabled={!canSubmit || saving}
            className="flex items-center gap-2 rounded-lg bg-primary px-4 py-2 text-sm font-semibold text-black transition hover:opacity-90 disabled:opacity-50"
          >
            <Save size={15} />
            {saving ? "Asignando..." : "Asignar rutina"}
          </button>
          <button
            onClick={onClose}
            className="flex items-center gap-2 rounded-lg border border-white/10 px-4 py-2 text-sm text-muted transition hover:text-white"
          >
            <X size={15} />
            Cancelar
          </button>
        </div>
      </motion.div>
    </motion.div>
  )
}
